import { store } from '../stores/store';

// Exchange rates relative to EUR (base currency)
export const EXCHANGE_RATES = {
    EUR: 1,
    DKK: 7.45,
    USD: 1.09,
    GBP: 0.86,
    NOK: 11.32,
    SEK: 11.85
};

// Locale-specific formatting rules
const LOCALE_FORMATS = {
    DKK: {
        million: 'mio.',
        thousand: 't.',
        position: 'after',
        space: true,
    },
    EUR: {
        million: 'M',
        thousand: 'K',
        position: 'before',
        space: false,
    },
    USD: {
        million: 'M',
        thousand: 'K',
        position: 'before',
        space: false,
    },
    GBP: {
        million: 'M',
        thousand: 'K',
        position: 'before',
        space: false,
    },
    NOK: {
        million: 'mill.',
        thousand: 't.',
        position: 'after',
        space: true,
    },
    SEK: {
        million: 'milj.',
        thousand: 't.',
        position: 'after',
        space: true,
    }
};

// Currency symbols and codes
export const CURRENCY_SYMBOLS = {
    EUR: '€',
    DKK: 'kr',
    USD: '$',
    GBP: '£',
    NOK: 'kr',
    SEK: 'kr'
};

export const formatCurrency = (amount, forcedCurrency = null) => {
    const currency = forcedCurrency || store.getState().auth.currency || 'EUR';

    const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return formatter.format(amount);
};

export const convertCurrency = (amount, fromCurrency = 'EUR', toCurrency = null) => {
    const targetCurrency = toCurrency || store.getState().auth.currency || 'EUR';

    if (fromCurrency === targetCurrency) {
        return amount;
    }

    const amountInEUR = amount / EXCHANGE_RATES[fromCurrency];
    return Math.round(amountInEUR * EXCHANGE_RATES[targetCurrency]);
};

const formatLocalizedPrice = (amount, currency, format) => {
    const symbol = CURRENCY_SYMBOLS[currency];
    const number = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    if (format.position === 'before') {
        return `${symbol}${format.space ? ' ' : ''}${number}`;
    }
    return `${number}${format.space ? ' ' : ''}${symbol}`;
};

const formatLocalizedSummary = (amount, currency) => {
    const format = LOCALE_FORMATS[currency];
    const symbol = CURRENCY_SYMBOLS[currency];

    if (amount >= 1000000) {
        const value = (amount / 1000000).toFixed(1).replace('.0', '');
        if (format.position === 'before') {
            return `${symbol}${format.space ? ' ' : ''}${value} ${format.million}`;
        }
        return `${value} ${format.million}${format.space ? ' ' : ''}${symbol}`;
    }

    if (amount >= 1000) {
        const value = Math.floor(amount / 1000);
        if (format.position === 'before') {
            return `${symbol}${format.space ? ' ' : ''}${value}${format.thousand}`;
        }
        return `${value} ${format.thousand}${format.space ? ' ' : ''}${symbol}`;
    }

    return formatLocalizedPrice(amount, currency, format);
};

export const formatPrice = (amount, fromCurrency = 'EUR', options = {}) => {
    const { summarize = false } = options;

    if (!amount) return 'N/A';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return 'N/A';

    const targetCurrency = store.getState().auth.currency || 'EUR';
    const convertedAmount = convertCurrency(numAmount, fromCurrency, targetCurrency);

    if (summarize) {
        return formatLocalizedSummary(convertedAmount, targetCurrency);
    }

    return formatLocalizedPrice(convertedAmount, targetCurrency, LOCALE_FORMATS[targetCurrency]);
};

export const formatPriceSummary = (amount, fromCurrency = 'EUR') => {
    return formatPrice(amount, fromCurrency, { summarize: true });
};